import type { Device, DeviceModel, DeviceType } from "../../api/types";
import type { UpdateDeviceRequest } from "../../api/devices";

function normaliseCatalogValue(value: string) {
  return value.trim().toLowerCase().replace(/[\s_]+/g, "-");
}

function findModelInCatalog(models: DeviceModel[], model: string) {
  const normalised = normaliseCatalogValue(model);
  if (!normalised) return undefined;
  return models.find((entry) =>
    normaliseCatalogValue(entry.modelCode) === normalised ||
    normaliseCatalogValue(entry.name) === normalised,
  );
}

// inferDeviceTypeIdFromModel maps a device-reported model string to a device
// type id, or "" - the frontend copy of devicetype.InferDeviceTypeID.
// Separators are normalised to hyphens first, so "two_way_comms" and
// "teltonika_gateway" match the same patterns as their hyphenated forms.
export function inferDeviceTypeIdFromModel(model: string): string {
  const normalised = normaliseCatalogValue(model);
  if (
    normalised.includes("two-way-comms") ||
    normalised.includes("2-way-comms") ||
    normalised.includes("two-way-communication")
  ) {
    return "two_way_comms";
  }
  if (normalised.includes("teltonika-gateway")) {
    return "teltonika_gateway";
  }
  return "";
}

export function resolveDeviceTypeIdForModel(
  model: string,
  deviceModels: DeviceModel[],
  deviceTypes: DeviceType[],
) {
  const catalogModel = findModelInCatalog(deviceModels, model);
  if (catalogModel) {
    return { deviceTypeId: catalogModel.deviceTypeId, modelCode: catalogModel.modelCode, modelIsInCatalog: true };
  }

  // Mirrors devicetype.InferDeviceTypeID in the backend for models that were
  // never added to the catalog (fleet-provisioned devices report free-form
  // model strings).
  const inferred = inferDeviceTypeIdFromModel(model);
  if (inferred) {
    const inferredType = deviceTypes.find((entry) => entry.deviceTypeId === inferred);
    if (inferredType) {
      return { deviceTypeId: inferredType.deviceTypeId, modelCode: model, modelIsInCatalog: false };
    }
  }

  return { deviceTypeId: "", modelCode: model, modelIsInCatalog: false };
}

export function buildDeviceUpdateRequest({
  device,
  imei,
  name,
  contactNumber,
  groupId,
  deviceTypeId,
  model,
  description,
  deviceModels,
}: {
  device: Pick<Device, "deviceTypeId" | "model">;
  imei: string;
  name: string;
  contactNumber: string;
  groupId: string;
  deviceTypeId: string;
  model: string;
  description: string;
  deviceModels: DeviceModel[];
}): UpdateDeviceRequest {
  const body: UpdateDeviceRequest = {
    imei,
    deviceName: name,
    contactNumber: contactNumber.trim() || null,
    groupId: groupId || "",
    description: description.trim(),
  };

  const originalDeviceTypeId = (device.deviceTypeId ?? "").trim();
  const originalModel = (device.model ?? "").trim();
  const nextDeviceTypeId = deviceTypeId.trim();
  const nextModel = model.trim();
  const nextModelIsKnown = nextModel === "" || !!findModelInCatalog(deviceModels, nextModel);
  const modelChanged = nextModel !== originalModel;
  const typeChanged = nextDeviceTypeId !== originalDeviceTypeId;

  if (!nextDeviceTypeId) {
    if (originalDeviceTypeId || originalModel || modelChanged || typeChanged) {
      body.deviceTypeId = null;
      body.model = null;
    }
    return body;
  }

  if (nextModelIsKnown && (nextDeviceTypeId || originalDeviceTypeId || modelChanged || typeChanged)) {
    body.deviceTypeId = nextDeviceTypeId;
    body.model = nextModel || null;
  }

  return body;
}
